import React, { useState, useEffect, useMemo } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import Filters from "components/Common/Filters"
import Switch from "components/Shared/Switch"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import isPermissionEnabled from "common/isPermissionEnabled"
import TableShimmer from "components/Shared/TableShimmer"
import DeleteModal from "components/Common/DeleteModal"
import formatDateTime from "components/Shared/formatDateTime"
import { getAllZones, updateZoneStatus } from "store/actions/zoneAction"
import { getDeliveryProfilesScope } from "store/actions/globalSettingAction"
import { useTranslation } from "react-i18next"
import ConfirmModal from "components/Common/ConfirmModal"
import { brandUsesAsapDeliveryPattern } from "utils/deliveryProfileHelpers"

const Zones = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Zones List ")
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const countryParam = searchParams.get("country")
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
    country: countryParam
      ? (() => {
          const [label, value] = countryParam.split(":")
          return { label, value }
        })()
      : [],
    region: [],
  })
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    zones,
  } = useSelector(state => state.Zone)

  const { userRole, accountType } = useSelector(state => state.Login)
  const { deliveryProfilesScope } = useSelector(state => state.GlobalSettings)
  const hideDeliveryDaysColumns = useMemo(
    () => brandUsesAsapDeliveryPattern(deliveryProfilesScope?.cards),
    [deliveryProfilesScope?.cards]
  )

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusZoneId, setStatusZoneId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      43,
      "zone_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      43,
      "zone_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      43,
      "zone_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      43,
      "zone_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    dispatch(getDeliveryProfilesScope())
    getZones()
  }, [])

  const getZones = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllZones(payload))
    setIsApiCalled(true)
  }

  const handleFetchZones = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    dispatch(getAllZones(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/zones?${searchParams.toString()}`)
        }
      })
      .catch(() => {})
  }

  const handleSearch = () => {
    handleFetchZones(1)
  }

  const onPageChange = page => {
    handleFetchZones(page)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Enabled"), value: 1 },
        { label: t("Disabled"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllZones(payload))
    navigate(`/zones?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  const handleConfirmStatusUpdate = () => {
    if (!statusZoneId) return
    setSelectedId(statusZoneId)
    const payload = {
      id: statusZoneId,
      status: statusToUpdate,
    }
    dispatch(updateZoneStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
      .finally(() => {
        setShowStatusModal(false)
        setStatusZoneId(null)
        setStatusToUpdate(null)
      })
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: 2,
    }
    dispatch(updateZoneStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
          handleFetchZones(1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const getZoneActionItems = item => [
    ...(usersPermissions.edit
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`/zones/edit?id=${item._id}`),
          },
        ]
      : []),
    {
      key: "view",
      content: (
        <>
          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
          {t("View")}
        </>
      ),
      onClick: () => navigate(`/zones/zone-details?id=${item._id}`),
    },
    ...(usersPermissions.delete
      ? [
          {
            key: "delete",
            content: (
              <>
                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                {t("Delete")}
              </>
            ),
            onClick: () => {
              setItemIdToDelete(item._id)
              setShowDeleteModal(true)
            },
          },
        ]
      : []),
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  cardTitle={t("Zones List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add Zone")}
                  onAddBtnClick={() => navigate("/zones/add")}
                />
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    searchBy={t("Name")}
                    colSize={4}
                  />

                  <div className="table-responsive">
                    {loading ? (
                      <TableShimmer row={7} col={10} />
                    ) : zones.length === 0 ? (
                      <p className="text-center my-2">{t("No zones found")}</p>
                    ) : (
                      <Table className="table table-nowrap mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Zone Name")}</th>
                            <th>{t("City")}</th>
                            {!hideDeliveryDaysColumns ? (
                              <>
                                <th>{t("Minimum Delivery Days")}</th>
                                <th>{t("Maximum Delivery Days")}</th>
                              </>
                            ) : null}
                            <th>{t("Created At")}</th>
                            <th>{t("Updated At")}</th>
                            <th>{t("Delivery Enabled")}</th>
                            <th>{t("Status")}</th>
                            <th className="align-middle">{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {zones.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(`/zones/zone-details?id=${item._id}`)
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </th>
                              <td>
                                {language === "sa" ? item.nameAr : item.name}
                              </td>
                              <td>
                                {language === "sa"
                                  ? item?.city?.nameAr
                                  : item?.city?.name}
                              </td>
                              {!hideDeliveryDaysColumns ? (
                                <>
                                  <td>{item?.minDays || 0}</td>
                                  <td>{item?.maxDays || 0}</td>
                                </>
                              ) : null}
                              <td>{formatDateTime(item?.createdAt)}</td>
                              <td>{formatDateTime(item?.updatedAt)}</td>
                              <td>
                                {item?.deliveryEnabled ? (
                                  <Badge className="bg-success">
                                    {t("Enabled")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-secondary">
                                    {t("Disabled")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Enabled")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Disabled")}
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle">
                                <div className="d-inline-flex align-items-center gap-2">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        setStatusZoneId(item._id)
                                        setStatusToUpdate(
                                          item.status === 1 ? 0 : 1
                                        )
                                        setShowStatusModal(true)
                                      }}
                                      isChecked={item.status === 1}
                                      disabled={
                                        item._id === selectedId && isLoading
                                      }
                                    />
                                  )}
                                  <TableActionsDropdown
                                    items={getZoneActionItems(item)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && zones.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={zones}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Zone"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isLoading}
        onDeleteClick={handleConfirmStatusUpdate}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </React.Fragment>
  )
}

export default Zones
